import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { siteUrlsSelector } from '~selectors/SiteUrlsSelectors';
import { IntegrationsSubspaceName } from '~src/app/spaces/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { BackgroundJobsSpaceProvider } from '~src/backgroundJob/components/BackgroundJobsSpaceProvider';
import { AllSchedulesSpaceProvider } from '~src/schedule/components/AllSchedulesSpaceProvider';
import { ResourceGovernanceSubspaceName } from '~src/schedule/components/ResourceGovernanceSubspace';
import { ScheduleSpaceRouter } from '~src/schedule/components/ScheduleRouter';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { ServerUrls } from '../../ServerUrls';
import { AppState } from '../../state/AppState';
import { ServerSettingsSubspaceName } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from '../AnalysisVizSpace/AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { ConnectedReduxRouterErrorWrapper } from '../RouterHelpers/ReduxRouterErrorWrapper';
import { RouterError } from '../RouterHelpers/RouterError';
import { isIntegrationsTabEnabledForServerSettings } from '../ServerSettingsSpace/ServerSettingsSpace';
import { ServerSettingsSpaceProvider } from '../ServerSettingsSpace/ServerSettingsSpaceProvider';
import { ServerStatusSpaceProvider } from '../ServerStatusSpace/ServerStatusSpaceProvider';
import { ServerTasksSpaceRouter } from '../ServerTasksSpace/ServerTasksSpaceRouter';
import { ServerUserSpaceProvider } from '../ServerUser/ServerUserSpaceProvider';
import { AllServerUsersSpaceProvider } from '../ServerUsersSpace/AllServerUsersSpaceProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { AllServerUsersAndIdentitiesSpaceRouter } from '../UserIdentitiesSpace/ServerUsers/AllServerUsersAndIdentitiesSpaceRouter';
import { AllSitesSpaceProvider } from './Sites/AllSitesSpaceProvider';



export interface ServerSpaceRouterOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface ServerSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
}

export type ServerSpaceRouterComponentProps = ServerSpaceRouterOwnProps & ServerSpaceRouterStateProps;

export class ServerSpaceRouterComponent extends React.PureComponent<ServerSpaceRouterComponentProps> {
  private readonly server: SessionManagementService;

  public constructor(props: ServerSpaceRouterComponentProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <div className="tb-fill">
        <Switch>
          { this.server.isUserAuthorized(AuthLevel.ServerAdmin) &&
            this.routes()
          }
          <Route render={this.error}/>
        </Switch>
      </div>
    );
  }

  private routes = () => {
    return (
      <Switch>
        <Route path={ServerUrls.getInstance().sitesPath()} render={this.serverSitesSpace}/>
        {
          FeatureFlags.jobManagementEnabled &&
          <Route
            path={ServerUrls.getInstance().jobsPath()}
            render={(routeProps) => <BackgroundJobsSpaceProvider
              siteName={undefined}
              routeLocationSearch={routeProps.location.search}
              routeLocationPath={routeProps.location.pathname}
            />}
          />
        }
        <Route
          exact path={ServerUrls.getInstance().schedulesPath()}
          render={(routeProps) => <AllSchedulesSpaceProvider
            siteName={undefined}
            routeLocationSearch={routeProps.location.search}
            routeLocationPath={routeProps.location.pathname}
            urls={ServerUrls.getInstance()}
          />}
        />
        <Route
          path={ServerUrls.getInstance().schedulePath(':id')}
          render={() => <ScheduleSpaceRouter
            siteName={undefined}
            urls={ServerUrls.getInstance()}
          />}
        />
        {
          // Since the "/" is allowed in usernames historically back to early Tableau Server versions, we want to support
          // routing in these cases. However, by default, React Router doesn't handle this character in a way we want.
          // The "+" notation is a way to make the matching greedy, whereas without it we would break at all "/" characters,
          // which is the way React Router natively interprets matches.
          // See:
          // https://github.com/ReactTraining/react-router/issues/313
          // and
          // https://stackoverflow.com/questions/56133182/allow-slashes-in-route-parameter-on-react
        }
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userSettingsPath(':domainName', ':username+'))}
          render={(routeProps) => <ServerUserSpaceProvider
            domainName={routeProps.match.params.domainName}
            // Need to decode username as special characters like space, #, etc are allowed for an username and are decoded in the url
            userName={decodeURIComponent(routeProps.match.params.username)}
          />}
        />
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userPath(':domainName', ':username+'))}
          render={({match}) => <Redirect
            to={ServerUrls.getInstance().userSettingsPath(match.params.domainName, match.params.username)}/>}
        />

        {this.getAllServerUsersSpaceRoute()}

        {this.getServerStatusVizRoute(this.server)}
        {
          FeatureFlags.extensionsSettingsTabEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsExtensionsPath()}
                 render={this.settingsProvider('extensions')}/>
        }
        <Route exact path={ServerUrls.getInstance().settingsPath()} render={this.settingsProvider('settings')}/>
        <Route exact path={ServerUrls.getInstance().settingsLicensesPath()} render={this.settingsProvider('licenses')}/>
        {
          FeatureFlags.serverWelcomeChannelEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsCustomizationPath()}
                 render={this.settingsProvider('customization')}/>
        }
        {
          isIntegrationsTabEnabledForServerSettings() &&
          <Route exact path={ServerUrls.getInstance().settingsIntegrationsPath()}
                 render={this.settingsProvider(IntegrationsSubspaceName)}/>
        }
        {
          FeatureFlags.onPremiseGovernanceEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsResourceLimitsPath()}
            render={this.settingsProvider(ResourceGovernanceSubspaceName)}/>
        }
        {
          !this.server.isSessionMultiSite() &&
          <Route exact path={ServerUrls.getInstance().addSitePath()} render={this.settingsProvider('addsite')}/>
        }
        <Route
          path={ServerUrls.getInstance().statusPath()}
          render={() => <ServerStatusSpaceProvider/>}
        />
        <Route path={ServerUrls.getInstance().tasksPath()} render={() => <ServerTasksSpaceRouter {...this.props} />}/>
        <Redirect exact from={ServerUrls.getInstance().serverPath('')} to={ServerUrls.getInstance().statusPath()}/>
        <Route render={this.error}/>
      </Switch>
    );
  }

  private error = (): JSX.Element => {
    if (this.server.isUserAuthorized(AuthLevel.ServerAdmin)) {
      return <RouterError errorType={'server-default'} urls={this.props.urls}/>;
    } else {
      return <RouterError errorType={'default'} urls={this.props.urls}/>;
    }
  }

  private settingsProvider(subspaceName: ServerSettingsSubspaceName) {
    return () =>
      <ServerSettingsSpaceProvider
        subspaceName={subspaceName}
      />;
  }

  private serverSitesSpace = (props: RouteComponentProps): JSX.Element => (
    <AllSitesSpaceProvider
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )

  private getServerStatusVizRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={ServerUrls.getInstance().serverStatusVizPath(':sheetUrlId')}
             render={(routeProps) => <AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.ConnectedUpdater
                sheetUrlId= {routeProps.match.params.sheetUrlId}
                routeLocationSearch={routeProps.location.search}
                history={routeProps.history}
                urls={this.props.urls}
                isServerAnalysis={true}
                renderSpaceProvider={AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.renderProvider}
             />}
      />;
  }

  private getAllServerUsersSpaceRoute() {
    if (FeatureFlags.identityMigrationEnabled) {
      // Looks for a partial URL match and delegates on the user space router.
      return <Route
        path={ServerUrls.getInstance().usersPath()}
        render={(routeProps) => <AllServerUsersAndIdentitiesSpaceRouter
          urls={this.props.urls}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
    } else {
      // Looks for the exact URL match and renders the space.
      return <Route
        exact path={ServerUrls.getInstance().usersPath()}
        render={(routeProps) => <AllServerUsersSpaceProvider
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
    }
  }
}

const mapStateToProps: MapStateToProps<ServerSpaceRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  routerReactKey: state.routerSpaceReactKey
});

export const ConnectedServerSpaceRouter: React.ComponentClass<ServerSpaceRouterOwnProps>
  = connect(mapStateToProps)(ServerSpaceRouterComponent);

export interface ServerSpaceRouterProps extends ServerSpaceRouterOwnProps {}

export class ServerSpaceRouter extends React.PureComponent<ServerSpaceRouterProps> {
  public render() {
    return (
      <ConnectedReduxRouterErrorWrapper>
        <ConnectedServerSpaceRouter
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
        />
      </ConnectedReduxRouterErrorWrapper>
    );
  }
}
